import React, { useState, useEffect } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Micro } from "@vds/typography";
import verizon_logo from "../../assets/images/VerizonConnect_GlowWordmark_RGB.svg";
import helpIcon from "../../assets/icons/Help.svg";
import dashboardIcon from "../../assets/icons/dashboard.svg";
import * as Actions from "../../store/actions/global/global";
import NotificationBanner from "../notificationBanner/NotificationBanner";
import { Header, FlexRow, FlexColumn, Img, Wrapper } from "../../constants/common/StyledGlobalConstants";
import UserDropDown from "./UserDropDown";
import { pendoTrack } from "../../util/pendoUtil";
import { withRouter } from "../../withRouter";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import styled from "styled-components";
import { saveStateForNewTab } from "../../util/saveState";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const CustomFlexColumn = styled(FlexColumn)`
  &:focus {
    border: 2px solid black;
    border-radius: 5px;
  }
`;

function ManageHeader(props) {
  const navigate = useNavigate();
  const { fullName, windowSize, accountName, headerAriaHidden, enableMetricsDashboard } = useSelector((state) => state.ACCOUNT);
  const dispatch = useDispatch();
  const [showUserDropDown, setShowUserDropDown] = useState(false);
  const [logo, setLogo] = useState("");
  const { t } = useTranslation("headerTranslation");
  const location = useLocation();

  useEffect(() => {
    if (fullName) {
      let name = fullName.split(" ");
      setLogo(name[0][0].toUpperCase() + (name[1] ? name[1][0].toUpperCase() : ""));
    }
  }, []);

  const logout = () => {
    navigate("/logout");
    dispatch(Actions.setLogout(true));
  };

  const handleHelpLink = () => {
    pendoTrack("Customer Portal | Help", {
      status: "Success",
      accountId: props.accountNumber,
    });
    return window.open("https://reveal-help.verizonconnect.com/", "_blank");
  };

  const handleDashboardLink = () => {
    saveStateForNewTab();
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const basePath = pathname.substring(0, pathname.indexOf("/customer-portal/") + "/customer-portal/".length);
    const dashboardLink = `${origin}${basePath}#/metrics-dashboard`;
    window.open(dashboardLink, "_blank");
  };

  const shouldAriaHidden = () => {
    return location.pathname === "/account/technicianAppointments/transferDevice" || location.pathname === "/account/technicianAppointments/removeDevice" || location.pathname.includes("/account/technicianAppointments/reschedule/") || headerAriaHidden;
  };

  return (
    <Header aria-label="Verizon Connect, GPS Fleet Tracking System" as="header">
      <FlexRow height="100%" justifyContent="space-between">
        <Wrapper
          display={windowSize.width >= DESKTOP ? "" : "flex"}
          flexDirection={windowSize.width >= DESKTOP ? "" : "column"}
          justifyContent={windowSize.width >= DESKTOP ? "" : "center"}
          alignItems={windowSize.width >= DESKTOP ? "" : "center"}
        >
          <Img
            width={windowSize.width >= TABLET ? "" : "8rem"}
            height="5rem"
            tabIndex="-1"
            aria-hidden="true"
            alt="Verizon Connect, GPS Fleet Tracking System"
            src={verizon_logo}
          />
        </Wrapper>

        <FlexRow gap="24px" alignItems="center" justifyContent="flex-end" marginRight={"1.5rem"} height="100%">
          {enableMetricsDashboard && (
            <CustomFlexColumn
              id="header-dashboard-link"
              onClick={handleDashboardLink}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleDashboardLink();
              }}
              alignItems="center"
              height="100%"
              justifyContent="flex-start"
              style={{ gap: "4px" }}
              cursor
              aria-hidden={shouldAriaHidden()}
              tabIndex="0"
            >
              <Img width="1.1rem" marginTop={windowSize.width >= DESKTOP ? "1.5rem" : "1.7rem"} src={dashboardIcon} alt="dashboard" aria-hidden="true" />
              <Micro bold>{t("Dashboard")}</Micro>
            </CustomFlexColumn>
          )}
          
          {!props.displayPlannedMaintenance && (
            <CustomFlexColumn
              id="header-help-link"
              onClick={handleHelpLink}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleHelpLink();
              }}
              alignItems="center"
              height="100%"
              justifyContent="flex-start"
              style={{ gap: "4px" }}
              cursor
              aria-hidden={shouldAriaHidden()}
              tabIndex="0"
            >
              <Img width="1.1rem" marginTop={windowSize.width >= DESKTOP ? "1.5rem" : "1.7rem"} src={helpIcon} alt="help" aria-hidden="true" />
              <Micro bold>{t("Help")}</Micro>
            </CustomFlexColumn>
          )}

          <UserDropDown
            showUserDropDown={showUserDropDown}
            setShowUserDropDown={setShowUserDropDown}
            logo={logo}
            logout={() => logout()}
            fullname={fullName}
            accountname={accountName}
            ariaHiddenValue={shouldAriaHidden()}
          />
        </FlexRow>
      </FlexRow>
      {props.bannerDisplayOnLanding && <NotificationBanner />}
      {props.bannerDisplayOnAll && <NotificationBanner />}
    </Header>
  );
}

ManageHeader.propTypes = {
  accountNumber: PropTypes.string,
  displayPlannedMaintenance: PropTypes.bool,
  bannerDisplayOnLanding: PropTypes.bool,
  bannerDisplayOnAll: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    regionId: state.ACCOUNT.regionId,
    bannerDisplayOnLanding: state.ACCOUNT.bannerDisplayOnLanding,
    bannerDisplayOnAll: state.ACCOUNT.bannerDisplayOnAll,
    activeCookie: state.ACCOUNT.activeCookie,
    displayPlannedMaintenance: state.ACCOUNT.displayPlannedMaintenance,
    accountNumber: state.ACCOUNT.accountNumber,
    accountId: state.ACCOUNT.account_id,
  };
};
const mapDispatchToProps = {
  getBannerMessage: Actions.getBannerMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ManageHeader));
